import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Expense {
  id: number
  expense_date: string | null
  user_name: string | null
  expense_type: string | null
  other_type: string | null
  amount: number
  paid_by: string | null
  paid_to: string | null
  preapproved: boolean
  notes: string | null
  has_receipt: boolean
  receipt_url: string | null
  receipt_filename: string | null
  receipt_content_type: string | null
  submitted: boolean
  submitted_at: string | null
  approved: boolean
  approved_at: string | null
  approved_by: string | null
  paid: boolean
  paid_at: string | null
  paid_reference: string | null
  created_at: string | null
}

interface Props {
  customer: Customer
  expense: Expense
  can_delete: boolean
}

export default function Show({ customer, expense, can_delete }: Props) {
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteReason, setDeleteReason] = useState("")
  const [deleting, setDeleting] = useState(false)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Expenses", href: `/customers/${customer.id}/expenses` },
    {
      title: "Details",
      href: `/customers/${customer.id}/expenses/${expense.id}`,
    },
  ]

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (date: string | null) => {
    return (
      formatDateUtil(date, {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }) || "-"
    )
  }

  const handleDelete = () => {
    if (!deleteReason.trim()) return
    setDeleting(true)
    router.delete(`/customers/${customer.id}/expenses/${expense.id}`, {
      data: { reason: deleteReason.trim() },
      onFinish: () => {
        setDeleting(false)
        setDeleteOpen(false)
      },
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Expense - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Expense Details</h1>
          {can_delete && (
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogTrigger asChild>
                <button className="bg-hpi-red hover:bg-hpi-red/80 rounded-md px-4 py-2 text-sm font-medium text-white">
                  Delete
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Expense</DialogTitle>
                  <DialogDescription>
                    Why are you deleting this expense? This action cannot be
                    undone.
                  </DialogDescription>
                </DialogHeader>
                <textarea
                  className="border-hpi-light-gray w-full rounded border p-2 text-sm"
                  rows={4}
                  placeholder="Enter reason for deletion..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  required
                />
                <DialogFooter>
                  <button
                    type="button"
                    className="border-hpi-light-gray rounded border px-4 py-2 text-sm"
                    onClick={() => setDeleteOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-sm text-white disabled:opacity-50"
                    onClick={handleDelete}
                    disabled={!deleteReason.trim() || deleting}
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Basic Info */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Basic Information</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Employee</dt>
                <dd>{expense.user_name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Type</dt>
                <dd>
                  {expense.expense_type ?? "-"}
                  {expense.other_type && (
                    <span className="text-hpi-med-gray ml-1 text-sm">
                      ({expense.other_type})
                    </span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Payment Method</dt>
                <dd>{expense.paid_by ?? "-"}</dd>
              </div>
              {expense.preapproved && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Pre-Approved</dt>
                  <dd className="text-hpi-dark-green">Yes</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Date</dt>
                <dd>{formatDate(expense.expense_date)}</dd>
              </div>
              {expense.paid_to && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Paid To</dt>
                  <dd>{expense.paid_to}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Amount</dt>
                <dd className="font-semibold">
                  {formatCurrency(expense.amount)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Receipt & Status */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Receipt & Status</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Receipt</dt>
                <dd>
                  {expense.receipt_url ? (
                    <a
                      href={expense.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hpi-active-blue inline-flex items-center gap-1 hover:underline"
                    >
                      {expense.receipt_content_type === "application/pdf" ? (
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                      {expense.receipt_filename}
                    </a>
                  ) : expense.has_receipt ? (
                    <span className="text-hpi-dark-green">Yes</span>
                  ) : (
                    <span className="text-hpi-med-gray">No</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Workflow Status */}
          <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
            <h2 className="mb-4 text-lg font-semibold">Workflow Status</h2>
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <h3 className="font-medium">Added</h3>
                <div className="text-hpi-med-gray mt-1 text-sm">
                  {formatDate(expense.created_at)}
                </div>
              </div>
              <div>
                <h3 className="font-medium">Submitted</h3>
                {expense.submitted ? (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    {formatDate(expense.submitted_at)}
                  </div>
                ) : (
                  <div className="text-hpi-red mt-1 text-sm">Unsubmitted</div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Approval</h3>
                {expense.approved_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Approved</div>
                    {expense.approved_by && (
                      <div className="text-hpi-med-gray">
                        by {expense.approved_by}
                      </div>
                    )}
                    <div className="text-hpi-med-gray">
                      {formatDate(expense.approved_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    Pending Approval
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Payment</h3>
                {expense.paid_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Paid</div>
                    {expense.paid_reference && (
                      <div className="text-hpi-med-gray">
                        Ref: {expense.paid_reference}
                      </div>
                    )}
                    <div className="text-hpi-med-gray">
                      {formatDate(expense.paid_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    Not Yet Paid
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {expense.notes && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Notes</h2>
              <p className="text-hpi-dark-gray whitespace-pre-wrap">
                {expense.notes}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            href={`/customers/${customer.id}/expenses`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Expenses
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
